import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  UserCircle, 
  Trash, 
  Pencil, 
  Plus, 
  CheckCircle, 
  XCircle, 
  CaretDown, 
  CaretUp, 
  FileText,
  Check,
  X,
  Key,
  ClipboardText
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MB_KEYS = ['All', 'MB1', 'MB2', 'MB3', 'MB4', 'MB5', 'MB6'];

const DEFAULT_PANEL_ACCESS = {
  history_filter: { enabled: true, date_range: 'All time', scope: 'both' },
  summary: { enabled: true, date_range: 'All time', scope: 'both' },
  excel_report: { enabled: true, date_range: 'All time', scope: 'both' },
  pdf_report: { enabled: true, date_range: 'All time', scope: 'both', download: true }
};

const SETTINGS_POWER_KEYS = [
  { key: 'client_list', label: 'Client List (→ Client Management)' },
  { key: 'client_directory', label: 'Client Directory' },
  { key: 'create_client', label: 'Create Client' },
  { key: 'appearance', label: 'Appearance' },
  { key: 'broadcast', label: 'Broadcast' },
  { key: 'status', label: 'Status' },
  { key: 'reports', label: 'Reports (Excel / Summary / PDF)' },
  { key: 'service_timelines', label: 'Service Timelines (Requests)' },
  { key: 'mobile_number_full', label: 'Mobile Number — Full View (else masked)' },
  { key: 'enquiry_handler_mb', label: 'Enquiry Handler — Assign to MB' },
];

const DEFAULT_SETTINGS_POWERS = {
  client_list: true,
  client_directory: true,
  create_client: true,
  appearance: false,
  broadcast: true,
  status: true,
  reports: true,
  service_timelines: false,
  mobile_number_full: false,
  enquiry_handler_mb: true
};

export default function HandlerManagement({ onViewLogs }) {
  const [handlers, setHandlers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableMBs, setAvailableMBs] = useState(['All', 'MB1', 'MB2', 'MB3', 'MB4', 'MB5', 'MB6']);

  // Modals & Inline Edit State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [inlineEditId, setInlineEditId] = useState(null); // ID of handler currently being edited inline (Image 2)

  // Accordion Expand States
  const [expandedPanelAccess, setExpandedPanelAccess] = useState({});
  const [expandedSettingsPowers, setExpandedSettingsPowers] = useState({});

  // Inline / Create Form fields
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editRole, setEditRole] = useState('deposit_handler');

  // Create Form fields
  const [createUsername, setCreateUsername] = useState('');
  const [createPhone, setCreatePhone] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [createRole, setCreateRole] = useState('deposit_handler');

  useEffect(() => {
    loadHandlers();
    fetchMBConfig();
  }, []);

  const fetchMBConfig = async () => {
    try {
      const res = await axios.get(`${API}/mb-config`);
      if (res.data?.mbs) {
        const activeMBs = res.data.mbs.filter(m => !m.hidden).map(m => m.id);
        setAvailableMBs(['All', ...activeMBs]);
      }
    } catch (err) {
      console.error('Failed to load MB config:', err);
    }
  };

  const loadHandlers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/users/handlers`);
      setHandlers(response.data?.handlers || response.data || []);
    } catch (error) {
      console.error('Failed to load handlers:', error);
      toast.error('Failed to load handlers');
    } finally {
      setLoading(false);
    }
  };

  // Start Inline Editing for a handler (Ref Image 2)
  const startInlineEdit = (handler) => {
    setInlineEditId(handler.id);
    setEditName(handler.username || '');
    setEditPhone(handler.phone || '');
    setEditPassword('');
    setEditRole(handler.role || 'deposit_handler');
  };

  const cancelInlineEdit = () => {
    setInlineEditId(null);
    setEditName('');
    setEditPhone('');
    setEditPassword('');
    setEditRole('deposit_handler');
  };

  // Save Inline Edit (Ref Image 2)
  const handleSaveInlineEdit = async (e) => {
    e.preventDefault();
    if (!editName.trim()) {
      toast.error('Name is required');
      return;
    }
    try {
      const res = await axios.put(`${API}/users/handlers/${inlineEditId}`, {
        username: editName.trim(),
        phone: editPhone.trim() || undefined,
        password: editPassword.trim() || undefined,
        role: editRole
      });
      const updated = res.data?.handler;
      if (updated) {
        setHandlers(prev => prev.map(h => h.id === updated.id ? { ...h, ...updated } : h));
      }
      toast.success('Handler updated successfully!');
      cancelInlineEdit();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update handler');
    }
  };

  // Create Handler Submission
  const handleCreateHandler = async (e) => {
    e.preventDefault();
    if (!createUsername.trim() || !createPassword.trim()) {
      toast.error('Username and Password are required');
      return;
    }
    try {
      await axios.post(`${API}/users/handlers`, {
        username: createUsername.trim(),
        phone: createPhone.trim() || undefined,
        password: createPassword,
        role: createRole
      });
      toast.success('Handler created successfully!');
      setShowCreateModal(false);
      setCreateUsername('');
      setCreatePhone('');
      setCreatePassword('');
      setCreateRole('deposit_handler');
      loadHandlers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create handler');
    }
  };

  const handleDeleteHandler = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete handler ${name}?`)) return;
    try {
      await axios.delete(`${API}/users/handlers/${id}`);
      toast.success('Handler deleted successfully');
      setHandlers(prev => prev.filter(h => h.id !== id));
    } catch (error) {
      toast.error('Failed to delete handler');
    }
  };

  const handleToggleActive = async (handler) => {
    try {
      const res = await axios.patch(`${API}/users/handlers/${handler.id}/toggle-active`);
      const updated = res.data?.handler;
      if (updated) {
        setHandlers(prev => prev.map(h => h.id === updated.id ? { ...h, ...updated } : h));
        toast.success(updated.active ? `${handler.username} activated` : `${handler.username} deactivated`);
      }
    } catch (error) {
      toast.error('Failed to toggle handler status');
    }
  };

  // Toggle MB Assignment Pill (Ref Image 3)
  const handleToggleMB = async (handler, mbKey) => {
    let currentMBs = Array.isArray(handler.assigned_mbs) ? [...handler.assigned_mbs] : [];

    if (mbKey === 'All') {
      if (currentMBs.includes('All')) {
        currentMBs = [];
      } else {
        currentMBs = ['All'];
      }
    } else {
      // Remove 'All' if toggling specific MBs
      currentMBs = currentMBs.filter(m => m !== 'All');
      if (currentMBs.includes(mbKey)) {
        currentMBs = currentMBs.filter(m => m !== mbKey);
      } else {
        currentMBs.push(mbKey);
      }
    }

    try {
      const res = await axios.patch(`${API}/users/handlers/${handler.id}/permissions`, {
        assigned_mbs: currentMBs
      });
      const updated = res.data?.handler;
      if (updated) {
        setHandlers(prev => prev.map(h => h.id === updated.id ? { ...h, ...updated } : h));
        toast.success(`Updated MB assignments for ${handler.username}`);
      }
    } catch (err) {
      toast.error('Failed to update MB assignments');
    }
  };

  // Update Panel Access Permissions (Ref Image 4)
  const handleUpdatePanelAccess = async (handler, featureKey, changes) => {
    const currentAccess = { ...DEFAULT_PANEL_ACCESS, ...(handler.panel_access || {}) };
    const currentFeature = { ...currentAccess[featureKey], ...changes };
    const updatedAccess = { ...currentAccess, [featureKey]: currentFeature };

    // Optimistic UI update
    setHandlers(prev => prev.map(h => h.id === handler.id ? { ...h, panel_access: updatedAccess } : h));

    try {
      await axios.patch(`${API}/users/handlers/${handler.id}/permissions`, {
        panel_access: updatedAccess
      });
    } catch (err) {
      toast.error('Failed to update panel access settings');
      loadHandlers();
    }
  };

  // Toggle Settings Powers Switch (Ref Image 5)
  const handleToggleSettingsPower = async (handler, powerKey) => {
    const currentPowers = { ...(handler.settings_powers || {}) };
    const updatedPowers = { ...currentPowers, [powerKey]: !currentPowers[powerKey] };

    // Optimistic UI update
    setHandlers(prev => prev.map(h => h.id === handler.id ? { ...h, settings_powers: updatedPowers } : h));

    try {
      await axios.patch(`${API}/users/handlers/${handler.id}/permissions`, {
        settings_powers: updatedPowers
      });
    } catch (err) {
      toast.error('Failed to update settings powers');
      loadHandlers();
    }
  };

  // Helper to count active settings powers for accordion header
  const getActivePowersCount = (settingsPowers) => {
    const powers = settingsPowers || {};
    return SETTINGS_POWER_KEYS.filter(pow => powers[pow.key] === true || powers[pow.key] === 'true').length;
  };

  const displayHandlers = handlers.filter(h => h.role !== 'super_admin');

  if (loading) {
    return <p className="text-gray-500 text-center py-6">Loading handlers...</p>;
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6 text-sm text-gray-800">
      
      {/* Create Button */}
      <button
        onClick={() => setShowCreateModal(true)}
        className="w-full bg-[#25D366] hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-xl flex items-center justify-center gap-2 shadow-sm transition-colors cursor-pointer"
      >
        <Plus size={20} weight="bold" /> Create New Handler
      </button>

      {/* ─── SECTION 1: ALL HANDLERS (LIST WITH INLINE EDIT FORM - Ref Image 1 & 2) ─── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <h3 className="font-bold text-gray-800 text-base">All Handlers ({displayHandlers.length})</h3>
        </div>

        <div className="p-6 space-y-4">
          {displayHandlers.map((handler) => {
            const isEditing = inlineEditId === handler.id;

            return (
              <div key={handler.id} className="border border-gray-200 rounded-xl bg-white overflow-hidden shadow-2xs">
                {isEditing ? (
                  /* ─── INLINE EDIT FORM (Ref Image 2) ─── */
                  <form onSubmit={handleSaveInlineEdit} className="p-5 space-y-4 bg-slate-50/50">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        required
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Handler Name"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Mobile Number (Login ID)</label>
                      <input
                        type="text"
                        required
                        value={editPhone}
                        onChange={(e) => setEditPhone(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Mobile Number"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">New Password (optional)</label>
                      <input
                        type="password"
                        value={editPassword}
                        onChange={(e) => setEditPassword(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500 placeholder-gray-400"
                        placeholder="Leave empty to keep current"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Role</label>
                      <select
                        value={editRole}
                        onChange={(e) => setEditRole(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="deposit_handler">Deposit Handler</option>
                        <option value="withdrawal_handler">Withdrawal Handler</option>
                        <option value="support_handler">Support Handler</option>
                        <option value="inquiry_handler">Inquiry Handler</option>
                        <option value="calling_handler">Calling Handler</option>
                      </select>
                    </div>

                    {/* Form Buttons */}
                    <div className="flex items-center gap-3 pt-2">
                      <button
                        type="submit"
                        className="flex-1 py-2.5 bg-[#25D366] hover:bg-green-600 text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-1.5"
                      >
                        <Check size={16} weight="bold" /> Save
                      </button>
                      <button
                        type="button"
                        onClick={cancelInlineEdit}
                        className="flex-1 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold rounded-lg transition-colors flex items-center justify-center gap-1.5"
                      >
                        <X size={16} weight="bold" /> Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  /* ─── NORMAL VIEW ITEM (Ref Image 1) ─── */
                  <div className="flex items-center justify-between p-4 bg-white hover:bg-gray-50/50 transition-colors">
                    <div className="space-y-1">
                      <h4 className="font-bold text-gray-800 text-base">{handler.username}</h4>
                      <p className="text-xs text-gray-400 font-mono">{handler.phone || 'No mobile'}</p>
                      <div className="inline-block px-2.5 py-0.5 bg-green-50 text-green-700 text-[11px] font-semibold rounded-full border border-green-200 capitalize">
                        {handler.role.replace(/_/g, ' ')}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => startInlineEdit(handler)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                        title="Edit Handler"
                      >
                        <Pencil size={20} />
                      </button>
                      <button
                        onClick={() => handleDeleteHandler(handler.id, handler.username)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                        title="Delete Handler"
                      >
                        <Trash size={20} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ─── SECTION 2: HANDLER MANAGEMENT CARDS (Ref Image 3, 4, 5) ─── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-4">
        <div>
          <h3 className="font-bold text-gray-800 text-lg">Handler Management</h3>
          <p className="text-xs text-gray-500 mt-0.5">Control handler access. Deactivated handlers cannot login.</p>
        </div>

        <div className="space-y-4">
          {displayHandlers.filter(h => h.role !== 'calling_handler').map((handler) => {
            const isActive = handler.active !== false;
            const assignedMBs = Array.isArray(handler.assigned_mbs) ? handler.assigned_mbs : [];
            const panelAccess = { ...DEFAULT_PANEL_ACCESS, ...(handler.panel_access || {}) };
            const settingsPowers = handler.settings_powers || {};
            const isPanelExpanded = expandedPanelAccess[handler.id];
            const isSettingsExpanded = expandedSettingsPowers[handler.id];
            const activePowersCount = getActivePowersCount(handler.settings_powers);

            return (
              <div key={handler.id} className="border border-green-200/80 rounded-2xl p-5 bg-[#f6fbf7] space-y-4 shadow-2xs">
                
                {/* ─── Top Header Row (Ref Image 3) ─── */}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 bg-gray-200/80 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <UserCircle size={32} weight="fill" className="text-gray-400" />
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5">
                        <h4 className="font-bold text-gray-900 text-base">{handler.username}</h4>
                        {isActive && <CheckCircle size={16} weight="fill" className="text-emerald-500" />}
                      </div>
                      <p className="text-xs text-gray-500 font-mono">{handler.phone || 'No mobile'}</p>
                      
                      {/* Badges */}
                      <div className="flex items-center gap-2 pt-0.5">
                        <span className="px-2.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded uppercase tracking-wide">
                          {handler.role.replace(/_/g, ' ')}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                          {isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions Right (Logs + Deactivate/Activate - Ref Image 3) */}
                  <div className="flex items-center gap-2">
                    {onViewLogs && (
                      <button
                        onClick={() => onViewLogs(handler.id)}
                        className="px-3.5 py-1.5 rounded-lg text-xs font-bold border border-blue-500 text-blue-600 hover:bg-blue-50 transition-colors flex items-center gap-1.5"
                      >
                        <FileText size={16} weight="bold" /> Logs
                      </button>
                    )}
                    <button
                      onClick={() => handleToggleActive(handler)}
                      className={`px-4 py-1.5 rounded-lg text-xs font-bold text-white transition-colors flex items-center gap-1.5 shadow-2xs ${
                        isActive ? 'bg-[#ef4444] hover:bg-red-600' : 'bg-[#10b981] hover:bg-emerald-600'
                      }`}
                    >
                      <XCircle size={16} weight="bold" />
                      {isActive ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </div>

                {/* ─── MBS Segment Selector Pills (Ref Image 3) ─── */}
                <div className="flex items-center gap-2 pt-1 flex-wrap">
                  <span className="text-xs font-bold text-gray-500 mr-1">MBS:</span>
                  {availableMBs.map((mbKey) => {
                    const isSelected = assignedMBs.includes(mbKey);
                    return (
                      <button
                        key={mbKey}
                        onClick={() => handleToggleMB(handler, mbKey)}
                        className={`px-3 py-1 rounded-full text-xs font-bold border transition-all cursor-pointer ${
                          isSelected
                            ? 'bg-[#7c3aed] text-white border-[#7c3aed] shadow-2xs'
                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-100'
                        }`}
                      >
                        {mbKey}
                      </button>
                    );
                  })}
                </div>

                {/* ─── ACCORDION TRIGGERS (Ref Image 3, 4, 5) ─── */}
                <div className="space-y-2 pt-1">
                  
                  {/* PANEL ACCESS Accordions (Ref Image 4) */}
                  <div>
                    <button
                      onClick={() => setExpandedPanelAccess(prev => ({ ...prev, [handler.id]: !prev[handler.id] }))}
                      className="flex items-center gap-1.5 text-xs font-bold text-slate-700 hover:text-slate-900 bg-white border border-slate-200 px-3 py-1.5 rounded-lg shadow-2xs transition-colors"
                    >
                      <ClipboardText size={16} className="text-slate-500" />
                      PANEL ACCESS {isPanelExpanded ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
                    </button>

                    {/* Collapsible Panel Access Details (Ref Image 4) */}
                    {isPanelExpanded && (
                      <div className="mt-2.5 p-4 bg-white border border-slate-200 rounded-xl space-y-3.5 shadow-inner">
                        
                        {/* 1. History & Filter */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 pb-3 border-b border-slate-100">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={panelAccess.history_filter.enabled}
                              onChange={(e) => handleUpdatePanelAccess(handler, 'history_filter', { enabled: e.target.checked })}
                              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                            />
                            <span className="font-bold text-xs text-slate-800">History & Filter</span>
                          </label>

                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[11px] font-semibold text-slate-400">DATE</span>
                              <select
                                value={panelAccess.history_filter.date_range}
                                onChange={(e) => handleUpdatePanelAccess(handler, 'history_filter', { date_range: e.target.value })}
                                className="px-2 py-1 border border-slate-200 rounded text-xs bg-white text-slate-700"
                              >
                                {['All time', 'Today', 'Weekly', 'Monthly', 'Custom'].map(d => <option key={d} value={d}>{d}</option>)}
                              </select>
                            </div>

                            <div className="flex items-center gap-1">
                              {['both', 'withdraw', 'deposit'].map(sc => (
                                <button
                                  key={sc}
                                  onClick={() => handleUpdatePanelAccess(handler, 'history_filter', { scope: sc })}
                                  className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold border transition-colors capitalize ${
                                    panelAccess.history_filter.scope === sc
                                      ? 'bg-blue-600 text-white border-blue-600'
                                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                                  }`}
                                >
                                  {sc === 'both' ? 'Both' : (sc === 'withdraw' ? 'Withdraw' : 'Deposit')}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* 2. Summary */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 pb-3 border-b border-slate-100">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={panelAccess.summary.enabled}
                              onChange={(e) => handleUpdatePanelAccess(handler, 'summary', { enabled: e.target.checked })}
                              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                            />
                            <span className="font-bold text-xs text-slate-800">Summary</span>
                          </label>

                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[11px] font-semibold text-slate-400">DATE</span>
                              <select
                                value={panelAccess.summary.date_range}
                                onChange={(e) => handleUpdatePanelAccess(handler, 'summary', { date_range: e.target.value })}
                                className="px-2 py-1 border border-slate-200 rounded text-xs bg-white text-slate-700"
                              >
                                {['All time', 'Today', 'Weekly', 'Monthly', 'Custom'].map(d => <option key={d} value={d}>{d}</option>)}
                              </select>
                            </div>

                            <div className="flex items-center gap-1">
                              {['both', 'withdraw', 'deposit'].map(sc => (
                                <button
                                  key={sc}
                                  onClick={() => handleUpdatePanelAccess(handler, 'summary', { scope: sc })}
                                  className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold border transition-colors capitalize ${
                                    panelAccess.summary.scope === sc
                                      ? 'bg-blue-600 text-white border-blue-600'
                                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                                  }`}
                                >
                                  {sc === 'both' ? 'Both' : (sc === 'withdraw' ? 'Withdraw' : 'Deposit')}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* 3. Excel Report */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 pb-3 border-b border-slate-100">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={panelAccess.excel_report.enabled}
                              onChange={(e) => handleUpdatePanelAccess(handler, 'excel_report', { enabled: e.target.checked })}
                              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                            />
                            <span className="font-bold text-xs text-slate-800">Excel Report</span>
                          </label>

                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[11px] font-semibold text-slate-400">DATE</span>
                              <select
                                value={panelAccess.excel_report.date_range}
                                onChange={(e) => handleUpdatePanelAccess(handler, 'excel_report', { date_range: e.target.value })}
                                className="px-2 py-1 border border-slate-200 rounded text-xs bg-white text-slate-700"
                              >
                                {['All time', 'Today', 'Weekly', 'Monthly', 'Custom'].map(d => <option key={d} value={d}>{d}</option>)}
                              </select>
                            </div>

                            <div className="flex items-center gap-1">
                              {['both', 'withdraw', 'deposit'].map(sc => (
                                <button
                                  key={sc}
                                  onClick={() => handleUpdatePanelAccess(handler, 'excel_report', { scope: sc })}
                                  className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold border transition-colors capitalize ${
                                    panelAccess.excel_report.scope === sc
                                      ? 'bg-blue-600 text-white border-blue-600'
                                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                                  }`}
                                >
                                  {sc === 'both' ? 'Both' : (sc === 'withdraw' ? 'Withdraw' : 'Deposit')}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* 4. PDF Report */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={panelAccess.pdf_report.enabled}
                              onChange={(e) => handleUpdatePanelAccess(handler, 'pdf_report', { enabled: e.target.checked })}
                              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                            />
                            <span className="font-bold text-xs text-slate-800">PDF Report</span>
                          </label>

                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[11px] font-semibold text-slate-400">DATE</span>
                              <select
                                value={panelAccess.pdf_report.date_range}
                                onChange={(e) => handleUpdatePanelAccess(handler, 'pdf_report', { date_range: e.target.value })}
                                className="px-2 py-1 border border-slate-200 rounded text-xs bg-white text-slate-700"
                              >
                                {['All time', 'Today', 'Weekly', 'Monthly', 'Custom'].map(d => <option key={d} value={d}>{d}</option>)}
                              </select>
                            </div>

                            <div className="flex items-center gap-1">
                              {['both', 'withdraw', 'deposit'].map(sc => (
                                <button
                                  key={sc}
                                  onClick={() => handleUpdatePanelAccess(handler, 'pdf_report', { scope: sc })}
                                  className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold border transition-colors capitalize ${
                                    panelAccess.pdf_report.scope === sc
                                      ? 'bg-blue-600 text-white border-blue-600'
                                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                                  }`}
                                >
                                  {sc === 'both' ? 'Both' : (sc === 'withdraw' ? 'Withdraw' : 'Deposit')}
                                </button>
                              ))}
                            </div>

                            <label className="flex items-center gap-1 cursor-pointer ml-1">
                              <input
                                type="checkbox"
                                checked={panelAccess.pdf_report.download !== false}
                                onChange={(e) => handleUpdatePanelAccess(handler, 'pdf_report', { download: e.target.checked })}
                                className="w-3.5 h-3.5 text-blue-600 rounded"
                              />
                              <span className="text-xs font-semibold text-slate-700">Download</span>
                            </label>
                          </div>
                        </div>

                      </div>
                    )}
                  </div>

                  {/* SETTINGS POWERS Accordion (Ref Image 5) */}
                  <div>
                    <button
                      onClick={() => setExpandedSettingsPowers(prev => ({ ...prev, [handler.id]: !prev[handler.id] }))}
                      className="flex items-center gap-1.5 text-xs font-bold text-slate-700 hover:text-slate-900 bg-white border border-slate-200 px-3 py-1.5 rounded-lg shadow-2xs transition-colors"
                    >
                      <Key size={16} className="text-amber-500" />
                      Settings Powers ({activePowersCount}/{SETTINGS_POWER_KEYS.length}) {isSettingsExpanded ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
                    </button>

                    {/* Collapsible Settings Powers Details (Ref Image 5) */}
                    {isSettingsExpanded && (
                      <div className="mt-2.5 p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2 shadow-inner font-sans">
                        <div className="text-[10px] font-bold text-indigo-700 uppercase tracking-wider mb-2">
                          GRANT / REVOKE SETTINGS ACCESS
                        </div>

                        <div className="space-y-1.5">
                          {SETTINGS_POWER_KEYS.map((pow) => {
                            const isON = settingsPowers[pow.key] === true || settingsPowers[pow.key] === 'true';

                            return (
                              <div
                                key={pow.key}
                                onClick={() => handleToggleSettingsPower(handler, pow.key)}
                                className={`flex items-center justify-between p-2.5 rounded-lg border cursor-pointer transition-all ${
                                  isON
                                    ? 'bg-[#00b87c] text-white border-[#00b87c] shadow-2xs font-bold'
                                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100 font-medium'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <div className={`w-4 h-4 rounded flex items-center justify-center ${isON ? 'bg-white/20' : 'border border-slate-300 bg-white'}`}>
                                    {isON && <Check size={12} weight="bold" className="text-white" />}
                                  </div>
                                  <span className="text-xs">{pow.label}</span>
                                </div>

                                <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${
                                  isON ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-400'
                                }`}>
                                  {isON ? 'ON' : 'OFF'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ─── CREATE HANDLER MODAL (NO QUEUE/CHAT TYPE FIELD) ─── */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl space-y-4">
            <h3 className="text-lg font-bold text-gray-800">Create New Handler</h3>
            
            <form onSubmit={handleCreateHandler} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Username</label>
                <input
                  type="text"
                  required
                  value={createUsername}
                  onChange={(e) => setCreateUsername(e.target.value)}
                  className="w-full px-4 py-2.5 border rounded-xl outline-none focus:border-green-500"
                  placeholder="Enter username"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Phone</label>
                <input
                  type="text"
                  required
                  value={createPhone}
                  onChange={(e) => setCreatePhone(e.target.value)}
                  className="w-full px-4 py-2.5 border rounded-xl outline-none focus:border-green-500"
                  placeholder="Enter mobile number"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Password</label>
                <input
                  type="password"
                  required
                  value={createPassword}
                  onChange={(e) => setCreatePassword(e.target.value)}
                  className="w-full px-4 py-2.5 border rounded-xl outline-none focus:border-green-500"
                  placeholder="Enter password"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Role</label>
                <select
                  value={createRole}
                  onChange={(e) => setCreateRole(e.target.value)}
                  className="w-full px-4 py-2.5 border rounded-xl outline-none focus:border-green-500 bg-white text-sm"
                >
                  <option value="deposit_handler">Deposit Handler</option>
                  <option value="withdrawal_handler">Withdrawal Handler</option>
                  <option value="support_handler">Support Handler</option>
                  <option value="inquiry_handler">Inquiry Handler</option>
                  <option value="calling_handler">Calling Handler</option>
                </select>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 py-2.5 border rounded-xl text-gray-500 font-semibold hover:bg-gray-50 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2.5 bg-[#25D366] text-white rounded-xl font-semibold hover:bg-green-600 cursor-pointer"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
